import React, { useEffect, useState, useCallback, useRef, memo } from 'react';
import ReactDOM from 'react-dom';
import Dashboard from './Dashboard';
import DashboardService from '../services/DashboardService';
import { GlobalVariablesProvider } from '../contexts/GlobalVariablesContext';
import './DashboardPopup.css';

/**
 * DashboardPopup 컴포넌트
 * 팝업 형태로 대시보드를 표시하는 컴포넌트
 * 
 * @param {Object} props
 * @param {Object} props.popup - 팝업 정보 객체
 * @param {boolean} props.isActive - 활성 상태 여부
 * @param {Function} props.onClose - 닫기 콜백
 * @param {Function} props.onFocus - 포커스 콜백
 * @param {Function} props.onMinimize - 최소화 콜백
 * @param {Function} props.onMaximize - 최대화 콜백
 * @param {Function} props.onPositionChange - 위치 변경 콜백
 * @param {Function} props.onSizeChange - 크기 변경 콜백
 */
const DashboardPopup = memo(({
  popup,
  isActive,
  onClose,
  onFocus,
  onMinimize,
  onMaximize,
  onPositionChange,
  onSizeChange,
  onLoadComplete,
  onError
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(popup.position);
  const [currentSize, setCurrentSize] = useState(popup.size);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);
  const loadCompleteFiredRef = useRef(false);
  
  // 탭 관련 상태 추가
  const [activeTab, setActiveTab] = useState(0);
  const [loadedTabs, setLoadedTabs] = useState([0]); // 처음엔 첫 번째 탭만 로드
  const [tabErrors, setTabErrors] = useState({}); // 탭별 에러 상태 관리
  
  const popupRef = useRef(null);
  const headerRef = useRef(null);
  const resizeHandleRef = useRef(null);
  
  // 멀티 대시보드 모드 확인
  const isMultiDashboard = popup.dashboards && Array.isArray(popup.dashboards) && popup.dashboards.length > 0;
  const dashboards = isMultiDashboard ? popup.dashboards : [
    {
      id: popup.dashboardId,
      name: popup.dashboardName || '대시보드',
      variables: popup.variables
    }
  ];
  
  // 투명 모드 확인
  const isTransparentMode = popup.transparentMode || popup.options?.transparentMode || false;
  
  // 탭 변경 핸들러
  const handleTabChange = useCallback((index) => {
    // 탭 전환 전 모든 ECharts 차트의 툴팁 숨기기
    const hideAllTooltips = () => {
      const charts = document.querySelectorAll('[_echarts_instance_]');
      charts.forEach(chartDom => {
        const chart = window.echarts?.getInstanceByDom(chartDom);
        if (chart && !chart.isDisposed()) {
          try {
            chart.dispatchAction({ type: 'hideTip' });
            chart.dispatchAction({ type: 'downplay' });
          } catch (e) {
            // 실패 시 무시
          }
        }
      });
    };
    
    hideAllTooltips();
    
    setActiveTab(index);
    // 아직 로드되지 않은 탭이면 로드 목록에 추가
    if (!loadedTabs.includes(index)) {
      setLoadedTabs(prev => [...prev, index]);
    }
    // 탭 전환 시 팝업 에러 상태 초기화
    if (popup.error) {
      // Context의 에러 상태를 초기화하도록 요청
      if (onError) {
        onError(popup.id, null); // null을 전달하여 에러 초기화
      }
    }
    
    // 탭 전환 후 차트 리사이즈 이벤트 발생
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      // 추가로 커스텀 이벤트도 발생
      const event = new CustomEvent('dashboardTabChanged', { 
        detail: { tabIndex: index, dashboardId: dashboards[index]?.id } 
      });
      window.dispatchEvent(event);
    }, 100); // 약간의 지연 후 리사이즈 이벤트 발생
  }, [loadedTabs, popup.error, popup.id, onError, dashboards]);
  
  // 대시보드 로드 상태 처리 (실제 로드는 Dashboard 컴포넌트에서)
  useEffect(() => {
    const currentDashboard = dashboards[activeTab];
    // 안전장치: 10초 후에도 로딩 중이면 에러로 처리
    const timeoutId = setTimeout(() => {
      if (popup.isLoading && !dashboardLoaded) {
        console.error(`[DashboardPopup] 대시보드 로드 타임아웃: ${currentDashboard?.id}`);
        if (onError) {
          onError(popup.id, {
            message: '대시보드 로드 시간 초과',
            dashboardId: currentDashboard?.id,
            status: 'timeout'
          });
        }
      }
    }, 10000);
    
    return () => clearTimeout(timeoutId);
  }, [dashboards, activeTab, popup.isLoading, popup.id, dashboardLoaded, onError]);
  
  // 위치 계산
  const calculatePosition = useCallback(() => {
    const { position } = popup;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    let x, y;
    
    if (position.x === 'center') {
      const popupWidth = typeof currentSize.width === 'string' 
        ? (windowWidth * parseFloat(currentSize.width) / 100)
        : currentSize.width;
      x = (windowWidth - popupWidth) / 2;
    } else if (position.x === 'auto') {
      x = windowWidth - 100; // 오른쪽 정렬
    } else {
      x = typeof position.x === 'number' ? position.x : 0;
    }
    
    if (position.y === 'center') {
      const popupHeight = typeof currentSize.height === 'string'
        ? (windowHeight * parseFloat(currentSize.height) / 100)
        : currentSize.height;
      y = (windowHeight - popupHeight) / 2;
    } else if (position.y === 'auto') {
      y = windowHeight - 100; // 하단 정렬
    } else {
      y = typeof position.y === 'number' ? position.y : 0;
    }
    
    // 경계 검사: 팝업이 화면 밖으로 벗어나지 않도록 최소값 0 보장
    // 팝업 높이가 화면보다 크면 y가 음수가 되어 위로 벗어나는 문제 방지
    x = Math.max(0, x);
    y = Math.max(0, y);
    
    return { x, y };
  }, [popup.position, currentSize]);
  
  // 크기 계산
  const calculateSize = useCallback(() => {
    const { width, height } = currentSize;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    let actualWidth = typeof width === 'string' 
      ? (windowWidth * parseFloat(width) / 100)
      : width;
      
    let actualHeight = typeof height === 'string'
      ? (windowHeight * parseFloat(height) / 100)
      : height;
    
    // 최소/최대 크기 적용
    const { minWidth, minHeight, maxWidth, maxHeight } = popup.size;
    
    if (minWidth) actualWidth = Math.max(actualWidth, minWidth);
    if (minHeight) actualHeight = Math.max(actualHeight, minHeight);
    
    if (maxWidth) {
      const max = typeof maxWidth === 'string'
        ? (windowWidth * parseFloat(maxWidth) / 100)
        : maxWidth;
      actualWidth = Math.min(actualWidth, max);
    }
    
    if (maxHeight) {
      const max = typeof maxHeight === 'string'
        ? (windowHeight * parseFloat(maxHeight) / 100)
        : maxHeight;
      actualHeight = Math.min(actualHeight, max);
    }
    
    return { width: actualWidth, height: actualHeight };
  }, [currentSize, popup.size]);
  
  
  // 리사이즈 시작
  const handleResizeStart = useCallback((e) => {
    if (!popup.options.resizable || popup.isMaximized) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    onFocus();
  }, [popup.options.resizable, popup.isMaximized, onFocus]);
  
  // 리사이즈 중
  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;
    
    const rect = popupRef.current.getBoundingClientRect();
    const newWidth = e.clientX - rect.left;
    const newHeight = e.clientY - rect.top;
    
    // 최소 크기 적용
    const { minWidth = 400, minHeight = 300 } = popup.size;
    const boundedWidth = Math.max(minWidth, newWidth);
    const boundedHeight = Math.max(minHeight, newHeight);
    
    setCurrentSize({
      ...currentSize,
      width: boundedWidth,
      height: boundedHeight
    });
    
    if (onSizeChange) {
      onSizeChange(popup.id, { width: boundedWidth, height: boundedHeight });
    }
  }, [isResizing, popup.id, popup.size, currentSize, onSizeChange]);
  
  // 리사이즈 종료
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);
  
  
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'nwse-resize';
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);
  
  // ESC 키로 닫기
  useEffect(() => {
    if (!popup.options.closeOnEscape || !isActive) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [popup.options.closeOnEscape, isActive, onClose]);
  
  // 배경 클릭으로 닫기
  const handleBackdropClick = useCallback((e) => {
    if (popup.options.backdrop.closeOnClick && e.target === e.currentTarget) {
      onClose();
    }
  }, [popup.options.backdrop.closeOnClick, onClose]);
  
  // 팝업 클릭 시 포커스
  const handlePopupClick = useCallback(() => {
    if (!isActive) {
      onFocus();
    }
  }, [isActive, onFocus]);
  
  // 대시보드 로드 완료 처리 (ID별 1회만 호출)
  useEffect(() => {
    if (dashboardLoaded && !loadCompleteFiredRef.current) {
      loadCompleteFiredRef.current = true;
      if (onLoadComplete) {
        onLoadComplete(popup.id);
      }
    }
  }, [dashboardLoaded, popup.id]);
  
  // 스타일 계산
  const position = calculatePosition();
  const size = calculateSize();
  
  const popupStyle = {
    position: 'fixed',
    left: popup.isMaximized ? 0 : position.x,
    top: popup.isMaximized ? 0 : position.y,
    width: popup.isMaximized ? '100%' : size.width,
    height: popup.isMaximized ? '100%' : size.height,
    zIndex: popup.zIndex,
    display: popup.isMinimized ? 'none' : 'flex',
    flexDirection: 'column',
    transition: isResizing ? 'none' : 'all 0.3s ease',
    transform: popup.isMinimized ? 'scale(0)' : 'scale(1)',
    opacity: 1,
    // 투명 모드: 배경, 테두리, 그림자 제거
    background: isTransparentMode ? 'transparent' : '#ffffff',
    borderRadius: isTransparentMode ? '0' : '12px',
    boxShadow: isTransparentMode ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.3), 0 10px 30px rgba(0, 0, 0, 0.2)',
    overflow: isTransparentMode ? 'visible' : 'hidden'
  };
  
  const backdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // 투명 모드: 백드롭 배경 반투명
    backgroundColor: isTransparentMode 
      ? 'transparent' 
      : `rgba(0, 0, 0, ${popup.options.backdrop.opacity})`,
    backdropFilter: isTransparentMode ? 'none' : (popup.options.backdrop.blur ? 'blur(5px)' : 'none'),
    zIndex: popup.zIndex - 1,
    display: popup.parentId ? 'none' : 'block' // 중첩 팝업은 배경 없음
  };
  
  // 애니메이션 클래스
  const animationClass = `popup-animation-${popup.options.animation.type}`;
  
  return ReactDOM.createPortal(
    <>
      {/* 배경 오버레이 */}
      {!popup.parentId && (
        <div
          className="dashboard-popup-backdrop"
          style={backdropStyle}
          onClick={handleBackdropClick}
        />
      )}
      
      {/* 팝업 컨테이너 */}
      <div
        ref={popupRef}
        className={`dashboard-popup ${animationClass} ${isActive ? 'active' : ''} ${popup.isMaximized ? 'maximized' : ''} ${isTransparentMode ? 'transparent-mode' : ''}`}
        style={popupStyle}
        onClick={handlePopupClick}
      >
        {/* 팝업 헤더 제거됨 - 닫기 버튼을 본문 우측 상단에 플로팅으로 추가 */}
        <button
          className={`popup-floating-close-btn ${isTransparentMode ? 'transparent-mode-close' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          title="닫기"
          style={{
            position: 'absolute',
            top: isTransparentMode ? '-5px' : '10px',
            right: isTransparentMode ? '-5px' : '10px',
            zIndex: 1000
          }}
        >
          ✕
        </button>
        
        {/* 탭 네비게이션 (멀티 대시보드일 때만 표시) */}
        {isMultiDashboard && dashboards.length > 1 && (
          <div className="dashboard-popup-tabs">
            {dashboards.map((dashboard, index) => (
              <button
                key={dashboard.id}
                className={`tab-button ${activeTab === index ? 'active' : ''}`}
                onClick={() => handleTabChange(index)}
                title={dashboard.description || dashboard.name}
              >
                {dashboard.icon && <span className="tab-icon">{dashboard.icon}</span>}
                <span className="tab-label">{dashboard.name}</span>
                {dashboard.badge && (
                  <span className="tab-badge">{dashboard.badge}</span>
                )}
              </button>
            ))}
          </div>
        )}
        
        {/* 팝업 본문 - 클릭 이벤트 전파 차단하여 포커스 로직과의 충돌 방지 */}
        <div 
          className={`dashboard-popup-body ${isTransparentMode ? 'transparent-mode-body' : ''}`} 
          onClick={(e) => e.stopPropagation()}
          style={isTransparentMode ? { background: 'transparent' } : {}}
        >
          {tabErrors[activeTab] ? (
            <div className="popup-error-simple">
              <div className="error-icon">⚠️</div>
              <div className="error-content">
                <p className="error-main-message">대시보드를 찾을 수 없습니다</p>
                <p className="error-dashboard-id">ID: {dashboards[activeTab]?.id}</p>
              </div>
              <button className="error-close-btn" onClick={() => {
                // 에러 초기화
                setTabErrors(prev => ({ ...prev, [activeTab]: false }));
                // 다른 탭으로 전환 시도
                const nextTab = dashboards.findIndex((d, i) => i !== activeTab && !tabErrors[i]);
                if (nextTab !== -1) {
                  handleTabChange(nextTab);
                }
              }}>다른 탭 보기</button>
            </div>
          ) : (
            <>
              {/* 로딩 중이어도 Dashboard는 렌더링하여 API 호출 시작 */}
              {popup.isLoading && (
                <div className="popup-loading-overlay">
                  <div className="loading-spinner"></div>
                  <p>대시보드를 불러오는 중...</p>
                </div>
              )}
              
              {/* 탭별 대시보드 렌더링 - 대시보드가 팝업 크기에 맞게 자동 적응 */}
              <div className="dashboard-tabs-container">
                {dashboards.map((dashboard, index) => (
                  <div
                    key={`tab-${dashboard.id}-${index}`}
                    className={`dashboard-tab-pane ${activeTab === index ? 'active' : ''}`}
                    style={{
                      display: activeTab === index ? 'block' : 'none',
                      height: '100%',
                      width: '100%'
                    }}
                  >
                    {loadedTabs.includes(index) ? (
                      // ===== 팝업 자동 크기 모드 적용 =====
                      // sizeMode='auto'일 때 DashboardPopupContext에서 계산된 값들:
                      // 1. overrideLayout: 차트들을 (0,0) 기준으로 재배치한 레이아웃
                      // 2. overrideGridColumns: 바운딩 박스 너비를 컬럼 수로 사용
                      // 3. overrideRowHeight: 정사각형 그리드를 위한 행 높이
                      <Dashboard
                        dashboardId={dashboard.id}
                        isPopup={true}
                        currentPopupId={popup.id}
                        parentPopupId={popup.parentId}
                        initialVariables={dashboard.variables || popup.variables}
                        overrideLayout={popup.normalizedLayout}
                        overrideGridColumns={popup.overrideGridColumns}
                        overrideRowHeight={popup.overrideRowHeight}
                        transparentMode={isTransparentMode}
                        onLoad={() => {
                          setDashboardLoaded(true);
                        }}
                        onError={(error) => {
                          console.error(`[DashboardPopup] Dashboard 에러:`, error);
                          // 해당 탭에만 에러 상태 설정
                          setTabErrors(prev => ({ ...prev, [index]: true }));
                        }}
                      />
                    ) : (
                      <div className="tab-loading-placeholder">
                        <div className="placeholder-icon">⏳</div>
                        <p>탭을 클릭하여 대시보드를 로드하세요</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        
        {/* 리사이즈 핸들 */}
        {popup.options.resizable && !popup.isMaximized && (
          <div
            ref={resizeHandleRef}
            className="dashboard-popup-resize-handle"
            onMouseDown={handleResizeStart}
          />
        )}
      </div>
    </>,
    document.getElementById('modal-root') || document.body
  );
});

DashboardPopup.displayName = 'DashboardPopup';

export default DashboardPopup;